import { useEffect, useMemo, useState } from "react";

import useSearchProducts from "@/Apollo/hooks/useSearchProducts";
import { useLanguage } from "@/common/hooks/pages.hooks";
import { homepageForYouVariables } from "@/components/Homepage/hooks/useHomepageForYouProducts.hooks";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import { dedupeNewThisWeekHomepage } from "@/features/search/utils/new-this-week.utils";

const NO_IDS: ReadonlySet<string> = new Set();

/**
 * The homepage "New this week" cards, without the products the For-you block above already shows.
 *
 * The For-you list is read cache-only from the block's own query, so this sends no request and
 * does not undo the block's lazy hydration: it updates once the block's fetch lands. The dedupe
 * waits for mount, so the server HTML and the first client render are the same first twelve and
 * hydration never sees a different list. The page carries spares, so the count stays twelve.
 */
export const useHomepageNewThisWeekProducts = (
  products: ProductCardProduct[]
): ProductCardProduct[] => {
  const { language } = useLanguage();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data } = useSearchProducts({
    variables: homepageForYouVariables(language),
    fetchPolicy: "cache-only",
    skip: !mounted,
  });

  const forYouIds = useMemo(() => {
    const items = data?.searchProducts?.items;

    if (!mounted || !items) {
      return NO_IDS;
    }

    return new Set(items.flatMap(item => (item?.id ? [item.id] : [])));
  }, [data, mounted]);

  return useMemo(
    () => dedupeNewThisWeekHomepage(products, forYouIds),
    [products, forYouIds]
  );
};
